import React from 'react';
import PropTypes from 'prop-types';

import Table from '../table';
import GenericObjectArrayProps from '../../prop-types/generic-object';
import BPLink from '../bp-link';
import WatchStation from '../watch-station';
import StatComparison from '../stat-comparison';
import ExportModal from '../export-modal';
import RecentRatings from '../recent-ratings';
import StarRating from '../star-rating';
import ComparisonValue from '../stat-comparison/comparison-value';

import { compareNumber, compareString } from '../table/sort-helpers';

import styles from './styles.css';

export default function StationRatingsTable({
  isLoading: isLoadingProps,
  data,
  comparisonData,
  stations,
  onExport,
  comparison,
  onPageChange,
  onSortChange,
  categoryRatings,
  onFilterChange,
}) {
  const STATION_RATINGS_COLUMNS = [
    {
      title: '',
      render: template => (
        <WatchStation stationId={template.id} isWatched={template.watched} onToggle={handleOnWatchStationToggle} className={styles.watchIcon} />
      ),
      value: template => template.watched,
      colSpan: 1,
    },
    {
      title: 'GasBuddy Rating',
      renderTableCell: template => (
        <>
          <td>
            <div className={styles.starRatingContainer}>
              <span className={styles.starRatingNumber}>{template.star_rating === 0 ? 'N/A' : template.star_rating}</span>
              <span className={styles.starContainer}><StarRating starCount={template.star_rating} numberOfStars={1} /></span>
            </div>
          </td>
          <ComparisonValue
            value={template.star_rating}
            comparisonValue={template.comparison?.star_rating}
          />
        </>

      ),
      value: template => template.star_rating,
      colSpan: 2,
      sortFn: compareNumber,
    },
    {
      title: 'Station',
      render: template => (
        <BPLink to={`/client/station-profiles/station-profile?stationId=${template.id}`}>
          {`#${template.retailer_reference} - ${template.retailer_name}`}
        </BPLink>
      ),
      value: template => `{${template.retailer_reference} - ${template.retailer_name}}`,
      colSpan: 2,
      sortFn: compareString,
    },
    {
      title: 'Total Ratings',
      renderTableCell: template => (
        <StatComparison
          value={template.negative + template.neutral + template.positive}
          comparisonValue={template.comparison?.categories[0]?.total_rating || 0}
        />
      ),
      value: template => template.negative + template.neutral + template.positive,
      colSpan: 2,
      sortFn: compareNumber,
    },
    {
      title: 'Last 5 Ratings',
      render: template => <RecentRatings ratings={template.recent_ratings} stationId={template.id} />,
    },
    {
      title: 'Negative',
      value: template => template.negative,
      renderTableCell: template => (
        <StatComparison
          value={template.negative}
          comparisonValue={template.comparison?.categories[0]?.negative || 0}
        />
      ),
      colSpan: 2,
      sortFn: compareNumber,
    },
    {
      title: 'Neutral',
      value: template => template.neutral,
      renderTableCell: template => (
        <StatComparison
          value={template.neutral}
          comparisonValue={template.comparison?.categories[0]?.neutral || 0}
        />
      ),
      colSpan: 2,
      sortFn: compareNumber,
    },
    {
      title: 'Positive',
      value: template => template.positive,
      renderTableCell: template => (
        <StatComparison
          value={template.positive}
          comparisonValue={template.comparison?.categories[0]?.positive || 0}
        />
      ),
      colSpan: 2,
      sortFn: compareNumber,
    },
  ];

  const [stationRatings, setStationRatings] = React.useState(null);
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(isLoadingProps);
  const [categoryFilters, setCategoryFilters] = React.useState([]);

  React.useEffect(() => {
    if (!isLoadingProps) {
      populateBrandEnagementMap();
    }
    setIsLoading(isLoadingProps);
  }, [isLoadingProps]);

  React.useEffect(() => {
    if (categoryRatings?.length) {
      setCategoryFilters(categoryRatings.map(rc => ({ name: rc.name, filterFn: () => true })));
    }
  }, [categoryRatings?.length]);

  const populateBrandEnagementMap = () => {
    const singleStationComparison = (comparison === 'Prior Period' || comparison === 'Nearby Stations');

    const map = {};
    const stationMap = _.keyBy(stations, 'id');
    const stationRatingsMap = _.keyBy(data, 'id');
    let comparisonStationRatingsMap = {};

    if (singleStationComparison) {
      comparisonStationRatingsMap = _.keyBy(comparisonData, 'id');
    }

    Object.keys(stationMap).map(Number).forEach((stationId) => {
      map[stationId] = {
        ...stationMap[stationId],
        ...stationRatingsMap[stationId],
        comparison: singleStationComparison ? comparisonStationRatingsMap[stationId] : comparisonData,
      };
    });
    setStationRatings(Object.values(map));
  };

  const handleOnExportClick = () => {
    setShowExportModal(true);
  };

  const handleOnWatchStationToggle = ({ id, watched }) => {
    const affectedIndex = stationRatings.findIndex(be => be.id === id);
    stationRatings[affectedIndex].watched = watched;
    setStationRatings(stationRatings);
  };

  return (
    <div style={{ marginTop: '-32px' }}>
      <Table
        title="Station Performance Breakdown"
        loading={isLoading}
        paginator
        data={stationRatings}
        tableColumns={STATION_RATINGS_COLUMNS}
        defaultSortColumn={STATION_RATINGS_COLUMNS[1].title}
        prioritySortOrder
        minWidth={800}
        onExport={onExport}
        onCTAClick={handleOnExportClick}
        CTAText="Export Data"
        onPageChange={onPageChange}
        onSortChange={onSortChange}
        filters={categoryFilters}
        onFilterChange={onFilterChange}
        filterLabel="Rating Category"
      />
      <ExportModal show={showExportModal} onClose={() => setShowExportModal(false)} onExport={onExport} />
    </div>
  );
}

StationRatingsTable.propTypes = {
  isLoading: PropTypes.bool,
  data: GenericObjectArrayProps,
  comparisonData: GenericObjectArrayProps,
  stations: GenericObjectArrayProps,
  onExport: PropTypes.func,
  comparison: PropTypes.string,
  onPageChange: PropTypes.func,
  onSortChange: PropTypes.func,
  categoryRatings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  onFilterChange: PropTypes.func,
};

StationRatingsTable.defaultProps = {
  isLoading: true,
  data: null,
  comparisonData: null,
  stations: null,
  onExport: () => { },
  comparison: null,
  onPageChange: () => { },
  onSortChange: () => { },
  categoryRatings: null,
  onFilterChange: () => { },
};
